import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { PrimeNgModule } from '../../../core/shared/primeng.module';

export interface MultiLineDataset {
  label: string;
  data: number[];
  color?: string;
}

export interface MultiLineChartConfig {
  /** X-axis labels. Pass a string array per item for multi-line labels, e.g. [['MON', '(16 APR)'], ...] */
  labels: (string | string[])[];
  datasets: MultiLineDataset[];
  /** Unit appended to tooltip values, e.g. 'Tokens', '$'. Leave empty for plain numbers. */
  valueUnit?: string;
  /** Optional prefix prepended to every tooltip/axis value, e.g. '$' */
  currencyPrefix?: string;
  yAxisTitle?: string;
  xAxisTitle?: string;
}

@Component({
  selector: 'app-multi-line-chart',
  standalone: true,
  imports: [PrimeNgModule, CommonModule],
  templateUrl: './multi-line-chart.component.html',
  styleUrl: './multi-line-chart.component.scss',
})
export class MultiLineChartComponent implements OnInit, OnChanges {
  @Input() config!: MultiLineChartConfig;
  /** Optional card title rendered above the chart */
  @Input() title?: string;
  /** Height of the chart canvas. Accepts any CSS value, e.g. '360px', '50vh'. Defaults to '450px'. */
  @Input() chartHeight: string = '450px';

  chartData: any;
  chartOptions: any;
  chartPlugins: any[] = [];

  /** Default color palette matching the reference image */
  private readonly palette = [
    '#C105B3', // Design – hot pink / magenta
    '#DED6FF', // Execution – soft lavender
    '#2F76FF', // Reporting & Analytics – bright blue
    '#38369A', // Maintenance – dark navy
  ];

  ngOnInit(): void {
    this.buildChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && !changes['config'].firstChange) {
      this.buildChart();
    }
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────

  private formatTick(value: number, prefix = ''): string {
    if (value >= 1_000_000) {
      const m = value / 1_000_000;
      return `${prefix}${m % 1 === 0 ? m : m.toFixed(1)}M`;
    }
    if (value >= 1_000) {
      return `${prefix}${Math.round(value / 1_000)}k`;
    }
    return `${prefix}${this.roundToSignificantDigits(value)}`;
  }

  /**
   * Rounds to 2 significant digits and strips floating-point rounding
   * artifacts (e.g. 0.000049999999999999996 -> 0.00005), always in
   * fixed-point notation (never scientific).
   */
  private roundToSignificantDigits(value: number, digits = 2): number {
    if (value === 0) return 0;
    const magnitude = Math.floor(Math.log10(Math.abs(value)));
    const decimals = Math.max(digits, digits - 1 - magnitude);
    return Number(value.toFixed(Math.min(decimals, 20)));
  }

  // ─── Chart build ────────────────────────────────────────────────────────────

  private buildChart(): void {
    if (!this.config) return;

    const { labels, datasets, currencyPrefix = '', valueUnit = '', yAxisTitle, xAxisTitle } = this.config;

    this.chartData = {
      labels,
      datasets: datasets.map((ds, i) => {
        const color = ds.color ?? this.palette[i % this.palette.length];
        return {
          label: ds.label,
          data: ds.data,
          borderColor: color,
          backgroundColor: 'transparent',
          pointBackgroundColor: 'white',
          pointBorderColor: color,
          pointBorderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 7,
          pointHoverBackgroundColor: 'white',
          pointHoverBorderColor: color,
          pointHoverBorderWidth: 2.5,
          borderWidth: 2,
          tension: 0,
          fill: false,
        };
      }),
    };

    // Custom plugin: draws a dashed vertical line at the hovered point
    const verticalLinePlugin = {
      id: 'verticalLine',
      afterDraw: (chart: any) => {
        const activeElements = chart.getActiveElements();
        if (!activeElements || activeElements.length === 0) return;

        const { ctx, chartArea } = chart;
        const activePoint = activeElements[0];
        const x = activePoint.element.x;

        ctx.save();
        ctx.beginPath();
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.35)';
        ctx.lineWidth = 1;
        ctx.moveTo(x, chartArea.top);
        ctx.lineTo(x, chartArea.bottom);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.restore();
      },
    };

    this.chartPlugins = [verticalLinePlugin];

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          mode: 'nearest',
          intersect: true,
          backgroundColor: '#1E1B3A',
          titleColor: 'rgba(255,255,255,0.75)',
          bodyColor: '#FFFFFF',
          titleFont: { size: 12, weight: '400' },
          bodyFont: { size: 15, weight: '700' },
          padding: { x: 14, y: 10 },
          cornerRadius: 10,
          displayColors: true,
          boxWidth: 8,
          boxHeight: 8,
          usePointStyle: true,
          caretSize: 8,
          caretPadding: 6,
          callbacks: {
            title: (items: any[]) => items[0]?.label ?? '',
            label: (context: any) => {
              const value: number = context.raw ?? 0;
              const formatted = `${currencyPrefix}${value.toLocaleString()}`;
              const text = valueUnit ? `${formatted} ${valueUnit}` : formatted;
              return `${context.dataset.label}: ${text}`;
            },
            labelPointStyle: () => ({ pointStyle: 'circle', rotation: 0 }),
          },
        },
        datalabels: { display: false },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: '#858381',
            font: { size: 11, family: 'inherit' },
            maxRotation: 0,
            padding: 6,
          },
          ...(xAxisTitle ? { title: { display: true, text: xAxisTitle, color: '#858381', font: { size: 11 } } } : {}),
        },
        y: {
          grid: {
            color: '#E8E7E6',
            lineWidth: 1,
          },
          border: { display: false, dash: [4, 4] },
          beginAtZero: true,
          ticks: {
            color: '#858381',
            font: { size: 11, family: 'inherit' },
            padding: 8,
            callback: (value: any) => this.formatTick(value, currencyPrefix),
          },
          ...(yAxisTitle ? { title: { display: true, text: yAxisTitle, color: '#858381', font: { size: 11 } } } : {}),
        },
      },
    };
  }

  get legendItems(): { label: string; color: string }[] {
    if (!this.config?.datasets) return [];
    return this.config.datasets.map((ds, i) => ({
      label: ds.label,
      color: ds.color ?? this.palette[i % this.palette.length],
    }));
  }
}
